import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'
import {
  Scissors, ShoppingBag, Heart, AlertTriangle,
  Wifi, Monitor, Car, Home, Plus, X, Send,
  Clock, CheckCircle, XCircle
} from 'lucide-react'
import { useKidRequests, useCreateKidRequest, Request } from '@/hooks/useRequests'

const requestCategories = [
  {
    category: 'needs',
    title: 'I Need',
    icon: ShoppingBag,
    color: 'blue',
    items: [
      { type: 'haircut', label: 'Haircut', emoji: '✂️' },
      { type: 'clothes', label: 'New Clothes', emoji: '👕' },
      { type: 'shoes', label: 'New Shoes', emoji: '👟' },
      { type: 'school_supplies', label: 'School Supplies', emoji: '✏️' },
      { type: 'toiletries', label: 'Toiletries', emoji: '🧴' },
      { type: 'other_need', label: 'Something Else', emoji: '📦' }
    ]
  },
  {
    category: 'health',
    title: "I'm Sick",
    icon: Heart,
    color: 'red',
    items: [
      { type: 'headache', label: 'Headache', emoji: '🤕' },
      { type: 'stomach', label: 'Stomach Ache', emoji: '🤢' },
      { type: 'cold', label: 'Cold/Flu', emoji: '🤧' },
      { type: 'injury', label: 'Injury', emoji: '🩹' },
      { type: 'medicine', label: 'Need Medicine', emoji: '💊' },
      { type: 'doctor', label: 'Need Doctor', emoji: '👨‍⚕️' }
    ]
  },
  {
    category: 'broken',
    title: "Something's Broken",
    icon: AlertTriangle,
    color: 'yellow',
    items: [
      { type: 'internet', label: 'Internet/WiFi', emoji: '📡' },
      { type: 'computer', label: 'My Computer', emoji: '💻' },
      { type: 'phone', label: 'My Phone', emoji: '📱' },
      { type: 'car', label: 'My Car', emoji: '🚗' },
      { type: 'room', label: 'In My Room', emoji: '🛏️' },
      { type: 'bathroom', label: 'Bathroom', emoji: '🚿' }
    ]
  }
]

export default function KidRequests() {
  const { user } = useAuth()
  const [showNewRequest, setShowNewRequest] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [description, setDescription] = useState('')
  const [urgency, setUrgency] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium')

  // React Query hooks
  const { data: requests = [] } = useKidRequests()
  const createMutation = useCreateKidRequest()

  const handleSubmitRequest = async () => {
    if (!selectedType) {
      toast.error('Please select what you need')
      return
    }

    try {
      const category = requestCategories.find(c =>
        c.items.some(i => i.type === selectedType)
      )
      const item = category?.items.find(i => i.type === selectedType)

      await createMutation.mutateAsync({
        request_type: selectedType,
        title: item?.label || selectedType,
        description,
        urgency
      })

      toast.success('Request sent to parents!')
      setShowNewRequest(false)
      setSelectedCategory('')
      setSelectedType('')
      setDescription('')
      setUrgency('medium')
      // No need to manually reload - React Query handles cache invalidation
    } catch (error) {
      toast.error('Failed to send request')
    }
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'text-red-600 bg-red-50'
      case 'high': return 'text-orange-600 bg-orange-50'
      case 'medium': return 'text-yellow-600 bg-yellow-50'
      default: return 'text-green-600 bg-green-50'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'approved': return <CheckCircle className="w-5 h-5 text-blue-600" />
      case 'denied': return <XCircle className="w-5 h-5 text-red-600" />
      default: return <Clock className="w-5 h-5 text-gray-600" />
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">My Requests</h2>
        <button
          onClick={() => setShowNewRequest(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New Request
        </button>
      </div>

      {/* New Request Modal */}
      {showNewRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">What do you need?</h3>
                <button
                  onClick={() => {
                    setShowNewRequest(false)
                    setSelectedCategory('')
                    setSelectedType('')
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!selectedCategory ? (
                <div className="space-y-3">
                  {requestCategories.map(category => (
                    <button
                      key={category.category}
                      onClick={() => setSelectedCategory(category.category)}
                      className={`w-full p-4 rounded-lg border-2 hover:border-${category.color}-500 transition-colors text-left`}
                    >
                      <div className="flex items-center gap-3">
                        <category.icon className={`w-6 h-6 text-${category.color}-600`} />
                        <div>
                          <div className="font-semibold">{category.title}</div>
                          <div className="text-sm text-gray-600">
                            {category.items.length} options
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {!selectedType ? (
                    <>
                      <button
                        onClick={() => setSelectedCategory('')}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        ← Back to categories
                      </button>
                      <div className="grid grid-cols-2 gap-3">
                        {requestCategories
                          .find(c => c.category === selectedCategory)
                          ?.items.map(item => (
                            <button
                              key={item.type}
                              onClick={() => setSelectedType(item.type)}
                              className="p-3 rounded-lg border-2 hover:border-blue-500 transition-colors"
                            >
                              <div className="text-2xl mb-1">{item.emoji}</div>
                              <div className="text-sm font-medium">{item.label}</div>
                            </button>
                          ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setSelectedType('')}
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        ← Back to options
                      </button>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Add details (optional)
                          </label>
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Tell us more about what you need..."
                            className="w-full p-3 border rounded-lg resize-none"
                            rows={3}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            How urgent is this?
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {['low', 'medium', 'high', 'urgent'].map(level => (
                              <button
                                key={level}
                                onClick={() => setUrgency(level as any)}
                                className={`p-2 rounded-lg border-2 ${
                                  urgency === level 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-gray-200'
                                }`}
                              >
                                <div className="capitalize font-medium">{level}</div>
                                <div className="text-xs text-gray-600">
                                  {level === 'urgent' && 'Right now!'}
                                  {level === 'high' && 'Today please'}
                                  {level === 'medium' && 'This week'}
                                  {level === 'low' && 'When you can'}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <button
                          onClick={handleSubmitRequest}
                          disabled={createMutation.isPending}
                          className="w-full btn-primary flex items-center justify-center gap-2"
                        >
                          <Send className="w-5 h-5" />
                          {createMutation.isPending ? 'Sending...' : 'Send Request'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No requests yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Need something? Click "New Request" above!
            </p>
          </div>
        ) : (
          requests.map(request => (
            <div key={request.id} className="bg-white rounded-lg border p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(request.status)}
                    <h3 className="font-semibold">{request.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(request.urgency)}`}>
                      {request.urgency}
                    </span>
                  </div>
                  
                  {request.description && (
                    <p className="text-gray-600 mb-2">{request.description}</p>
                  )}
                  
                  <div className="text-sm text-gray-500">
                    Requested {new Date(request.created_at).toLocaleDateString()}
                  </div>
                  
                  {request.response && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm font-medium text-blue-900">Parent Response:</div>
                      <div className="text-sm text-blue-800 mt-1">{request.response}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}